import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MoireFullLogo, MoireSymbol } from "@/components/MoireLogo";
import SnapButton from "@/components/SnapButton";
import { Swatch } from "@/components/ColorSwatch";
import { wwAsset, ASSET } from "@/lib/categoryAssets";
import { useReduceMotion } from "@/lib/motion";
import { KC } from "@/lib/kccinemAssets";

const HERO = "https://images.unsplash.com/photo-1762504013915-c1faf57f291b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";
const EDITORIAL = "https://images.unsplash.com/photo-1623421536280-fa8ae906ed1d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";
const COLUMNS = "https://images.unsplash.com/photo-1769614552343-a86788218525?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

const fade = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const DEMO = [
  { r: "01", name: "Crisp White", hex: "#F7F5F0", s: "9.8", label: "Flowing Gown", form: "gown" },
  { r: "02", name: "Ivory", hex: "#F1EAD9", s: "9.6", label: "Fine Knit", form: "knit" },
  { r: "03", name: "Powder Blue", hex: "#AEC6DE", s: "9.3", label: "Relaxed Tee", form: "tee" },
  { r: "04", name: "Soft Pink", hex: "#D9AFA6", s: "9.0", label: "Contemporary Blouse", form: "blouse" },
];
// the demo "piece" rotates across varied silhouettes so no single garment
// anchors MOIRÉ's identity. DEMO/FALLBACK ONLY — a real uploaded photo (shown
// on /results, never here) always wins and never rotates.
const PIECE_ROTATION = [
  { name: "Emerald Gown", meta: "Evening · Silk · Draped", src: wwAsset({ color_name: "Emerald", color_hex: "#1F7A5A" }, "gown") },
  { name: "Powder-Blue Denim Skirt", meta: "Weekend · Denim · A-line", src: wwAsset({ color_name: "Powder Blue", color_hex: "#AEC6DE" }, "skirt_denim") },
  { name: "Oversized White Tee", meta: "Everyday · Cotton · Relaxed", src: wwAsset({ color_name: "Crisp White", color_hex: "#F7F5F0" }, "tee") },
  { name: "Ankara Print Dress", meta: "Statement · Wax Print · Flowing", src: ASSET.dress_ankara_print },
  { name: "Camel Wide-Leg Trousers", meta: "Studio · Crepe · Fluid", src: wwAsset({ color_name: "Camel", color_hex: "#C19A6B" }, "trousers_wide") },
];

function useReducedMotion() {
  return useReduceMotion();
}

export default function Home() {
  const reduced = useReducedMotion();
  const [pieceIdx, setPieceIdx] = useState(0);
  // rotation runs unless the visitor has chosen reduced motion (stillness)
  useEffect(() => {
    if (reduced) return;
    const t = setInterval(() => setPieceIdx((p) => (p + 1) % PIECE_ROTATION.length), 5000);
    return () => clearInterval(t);
  }, [reduced]);
  const piece = PIECE_ROTATION[pieceIdx];
  return (
    <div>
      {/* ---------------- HERO ---------------- */}
      <section className="relative min-h-[100svh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D] via-[#0D0D0D]/85 to-[#0D0D0D]" />
        </div>

        <div className="relative z-10 w-full max-w-3xl mx-auto px-5 md:px-8 text-center py-24">
          <h1 className="sr-only">MOIRÉ — Dress Up. Don't Mess Up. Your personal styling intelligence.</h1>

          <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} className="flex justify-center">
            <MoireFullLogo width={460} className="w-[300px] sm:w-[380px] md:w-[460px]" />
          </motion.div>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }}
            className="font-serif italic text-2xl md:text-3xl text-[#F5F2EA] mt-10">
            Your personal styling intelligence.
          </motion.p>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.8 }}
            className="font-sans text-sm md:text-base text-[#F5F2EA]/60 mt-5 max-w-md mx-auto leading-relaxed">
            Photograph one piece. MOIRÉ finds the colours, combinations and finishing touches that make it work.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-5 mt-12">
            <SnapButton testid="hero-snap" />
            <a href="#how" data-testid="hero-how"
              className="font-sans text-sm tracking-luxe uppercase text-[#F5F2EA] link-underline">
              See How It Works
            </a>
          </motion.div>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.05, duration: 0.9 }}
            className="font-sans text-[11px] tracking-luxe uppercase text-[#C8A97E] mt-10">
            One piece. Your best matches. Zero guesswork.
          </motion.p>
        </div>
      </section>

      {/* ---------------- PRODUCT DEMONSTRATION ---------------- */}
      <section className="border-t border-[#F5F2EA]/10 bg-[#0D0D0D]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-28 md:py-36">
          <motion.h2 {...fade} className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-center text-[#F5F2EA]">
            One piece.<br />Your best matches.<br /><span className="text-[#C8A97E]">Zero guesswork.</span>
          </motion.h2>

          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-center mt-20">
            {/* the item */}
            <motion.div {...fade}>
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">The piece</p>
              <div className="mt-4 relative aspect-[4/5] w-full max-w-sm border border-[#F5F2EA]/10 overflow-hidden bg-[#F5F2EA]" data-testid="demo-the-piece">
                <AnimatePresence initial={false}>
                  <motion.img key={piece.src} src={piece.src} alt={piece.name} loading="lazy"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    transition={{ duration: reduced ? 0 : 1.1, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover" />
                </AnimatePresence>
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#0D0D0D] to-transparent pointer-events-none">
                  <AnimatePresence mode="wait">
                    <motion.div key={piece.name}
                      initial={{ opacity: 0, y: reduced ? 0 : 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: reduced ? 0 : -8 }}
                      transition={{ duration: reduced ? 0 : 0.45, ease: "easeOut" }}>
                      <div className="font-serif text-3xl text-[#F5F2EA]" data-testid="demo-piece-name">{piece.name}</div>
                      <div className="font-sans text-xs tracking-wider uppercase text-[#8A8170] mt-1">{piece.meta}</div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>

            {/* the matches */}
            <motion.div {...fade}>
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-4">MOIRÉ finds</p>
              <div className="space-y-2.5">
                {DEMO.map((d, i) => (
                  <motion.div key={d.r} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}
                    className="flex items-center gap-4 border-b border-[#F5F2EA]/10 pb-2.5">
                    <span className="relative w-12 h-12 shrink-0 overflow-hidden border border-[#F5F2EA]/15 bg-[#F5F2EA]">
                      <img src={wwAsset({ color_name: d.name, color_hex: d.hex }, d.form)} alt={`${d.name} ${d.label.toLowerCase()}`} loading="lazy" className="w-full h-full object-cover" />
                      <span className="absolute bottom-0.5 right-0.5 h-3 w-3 rounded-full border border-white/70" style={{ backgroundColor: d.hex }} />
                    </span>
                    <span className="font-sans text-[10px] tracking-luxe text-[#8A8170]">{d.r}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-serif text-xl text-[#F5F2EA] truncate">{d.name}</span>
                      <span className="block font-sans text-[9px] tracking-luxe uppercase text-[#8A8170]">{d.label}</span>
                    </span>
                    <span className="font-serif text-2xl text-[#C8A97E]">{d.s}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 border border-[#C8A97E]/40 bg-[#111111] p-6">
                <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">MOIRÉ's Pick</p>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-4">
                    <span className="relative h-14 w-14 shrink-0 overflow-hidden border border-[#F5F2EA]/15 bg-[#F5F2EA]">
                      <img src={wwAsset({ color_name: "Crisp White", color_hex: "#F7F5F0" }, "gown")} alt="Crisp white flowing gown" loading="lazy" className="w-full h-full object-cover" />
                      <span className="absolute bottom-0.5 right-0.5 h-3 w-3 rounded-full border border-white/70" style={{ backgroundColor: "#F7F5F0" }} />
                    </span>
                    <div>
                      <div className="font-serif text-2xl text-[#F5F2EA]">Crisp White</div>
                      <div className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170]">Flowing Gown</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-4xl text-[#C8A97E] leading-none">9.8</div>
                    <div className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] mt-1">/ 10</div>
                  </div>
                </div>
                <p className="font-serif italic text-lg text-[#F5F2EA]/85 mt-4 leading-relaxed">
                  “One colour, many ways. Crisp white keeps the emerald fresh and modern — clean contrast without competing.”
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---------------- HOW MOIRÉ WORKS ---------------- */}
      <section id="how" className="border-t border-[#F5F2EA]/10 max-w-6xl mx-auto px-5 md:px-8 py-28 md:py-36">
        <motion.p {...fade} className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] text-center">How MOIRÉ works</motion.p>
        <div className="grid md:grid-cols-3 gap-10 md:gap-6 mt-14 relative">
          {[
            { n: "01", t: "Snap", d: "Show us the piece you want to wear." },
            { n: "02", t: "Match", d: "MOIRÉ finds its strongest combinations." },
            { n: "03", t: "Style", d: "Finish the look with confidence." },
          ].map((s, i) => (
            <motion.div key={s.n} {...fade} transition={{ ...fade.transition, delay: i * 0.12 }}
              className="relative text-center md:text-left">
              <div className="font-serif text-7xl text-[#C8A97E]/30">{s.n}</div>
              <h3 className="font-serif text-4xl text-[#F5F2EA] mt-3">{s.t}</h3>
              <p className="font-sans text-sm text-[#8A8170] mt-3 leading-relaxed max-w-xs mx-auto md:mx-0">{s.d}</p>
              {i < 2 && <div className="hidden md:block absolute top-10 -right-3 text-[#C8A97E]/40"><ArrowRight size={20} strokeWidth={1.25} /></div>}
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------------- DON'T MESS UP ---------------- */}
      <section className="relative overflow-hidden border-y border-[#F5F2EA]/10 bg-[#111111]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-28 md:py-36 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fade}>
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">Already dressed?</p>
            <h2 className="font-serif text-5xl md:text-6xl text-[#F5F2EA] mt-3 leading-[0.95]">
              Don't<br />mess up.
            </h2>
            <p className="font-sans text-sm md:text-base text-[#F5F2EA]/70 mt-6 max-w-md leading-relaxed">
              Show MOIRÉ two pieces and see whether they belong together — before you walk out the door.
            </p>
            <div className="mt-10">
              <SnapButton variant="outline" label="Check My Combination" to="/dont-mess-up" testid="home-dmu" icon={false} />
            </div>
          </motion.div>

          <motion.div {...fade} className="border border-[#C8A97E]/40 bg-[#0D0D0D] p-8 md:p-10">
            <div className="flex items-center justify-center gap-5">
              <div className="text-center">
                <span className="relative block h-24 w-24 overflow-hidden border border-[#F5F2EA]/15 bg-[#F5F2EA]">
                  <img src={wwAsset({ color_name: "Sage", color_hex: "#A7B49E" }, "knit")} alt="Sage knit" loading="lazy" className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 right-1 h-3 w-3 rounded-full border border-white/70" style={{ backgroundColor: "#A7B49E" }} />
                </span>
                <div className="font-sans text-xs text-[#8A8170] mt-2">Sage knit</div>
              </div>
              <span className="font-serif text-3xl text-[#8A8170]">+</span>
              <div className="text-center">
                <span className="relative block h-24 w-24 overflow-hidden border border-[#F5F2EA]/15 bg-[#F5F2EA]">
                  <img src={wwAsset({ color_name: "Camel", color_hex: "#C19A6B" }, "street")} alt="Camel jacket" loading="lazy" className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 right-1 h-3 w-3 rounded-full border border-white/70" style={{ backgroundColor: "#C19A6B" }} />
                </span>
                <div className="font-sans text-xs text-[#8A8170] mt-2">Camel jacket</div>
              </div>
            </div>
            <div className="gold-line my-8" />
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] text-center">MOIRÉ Match</p>
            <div className="font-serif text-6xl text-[#C8A97E] text-center mt-1">9.6</div>
            <div className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] text-center">/ 10</div>
            <p className="font-serif italic text-xl text-[#F5F2EA] text-center mt-6">These belong together.</p>
          </motion.div>
        </div>
      </section>

      {/* ---------------- FUTURE EXPERIENCE ---------------- */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-28 md:py-36">
        <motion.h2 {...fade} className="font-serif text-4xl md:text-5xl text-[#F5F2EA] text-center leading-tight">
          Your wardrobe. Your style. <span className="text-[#C8A97E]">Your MOIRÉ.</span>
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-6 mt-16">
          {[
            { t: "My Wardrobe", d: "Save your own pieces and let MOIRÉ style from what you already own.", to: "/wardrobe" },
            { t: "Style Me", d: "Complete outfits built for your occasion, weather and mood.", to: "/style-me" },
            { t: "Outfit Check", d: "Photograph your full look for an honest cohesion score.", to: "/outfit-check" },
          ].map((f, i) => (
            <motion.div key={f.t} {...fade} transition={{ ...fade.transition, delay: i * 0.1 }}>
              <Link to={f.to} className="group block border border-[#F5F2EA]/10 p-8 hover:border-[#C8A97E]/50 transition-colors duration-400 h-full">
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-3xl text-[#F5F2EA]">{f.t}</h3>
                  <MoireSymbol size={30} className="opacity-40 group-hover:opacity-90 transition-opacity" />
                </div>
                <p className="font-sans text-sm text-[#8A8170] mt-4 leading-relaxed">{f.d}</p>
                <span className="inline-block mt-6 font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] group-hover:text-[#E5C293] transition-colors">Explore &rarr;</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------------- PHILOSOPHY ---------------- */}
      <section className="relative overflow-hidden border-y border-[#F5F2EA]/10">
        <div className="absolute inset-0">
          <img src={EDITORIAL} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-[#0D0D0D]/75" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8 py-32 text-center">
          <motion.div {...fade}>
            <p className="font-serif text-3xl md:text-5xl leading-[1.15] text-[#F5F2EA]">
              Fashion isn't about following every rule.<br />
              <span className="text-[#F5F2EA]/70">It's about knowing when something works.</span>
            </p>
            <p className="font-serif italic text-2xl text-[#C8A97E] mt-8">MOIRÉ helps you see it.</p>
          </motion.div>
        </div>
      </section>

      {/* ---------------- KC-CINEM 003.1 TEASER ---------------- */}
      <section className="relative overflow-hidden border-y border-[#F5F2EA]/10 bg-[#0A0A0A]">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -left-24 top-1/2 -translate-y-1/2 h-[60vh] w-[60vh] rounded-full" style={{ background: "radial-gradient(circle, rgba(176,18,31,0.22), transparent 65%)" }} />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fade}>
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">Collection · KC-CINEM 003.1</p>
            <h2 className="font-serif text-4xl md:text-6xl text-[#F5F2EA] mt-4 leading-[0.95]">THE TECHCARE<br />REAPER</h2>
            <p className="font-sans text-sm text-[#F5F2EA]/60 mt-6 max-w-md leading-relaxed">
              A figure between worlds. An original fashion and costume concept created and curated by Kendi Collections for MOIRÉ.
            </p>
            <Link to="/collections/kc-cinem-003-1" data-testid="home-kc-cinem-link"
              className="inline-flex items-center gap-2 mt-8 font-sans text-xs tracking-luxe uppercase text-[#C8A97E] hover:text-[#E5C293] link-underline">
              Enter the collection <ArrowRight size={15} />
            </Link>
          </motion.div>
          <motion.div {...fade} className="flex justify-center lg:justify-end">
            <Link to="/collections/kc-cinem-003-1" className="block border border-[#F5F2EA]/10 bg-black overflow-hidden max-w-xs">
              <img src={KC.character.src} width={KC.character.w} height={KC.character.h} alt={KC.character.alt}
                loading="lazy" decoding="async" className="w-full h-auto object-contain" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ---------------- KENDI ---------------- */}
      <section className="py-20 text-center">
        <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">Powered by Kendi Collections</p>
        <p className="font-serif italic text-xl text-[#F5F2EA]/80 mt-3">Classic fashion for the classic person.</p>
      </section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section className="relative overflow-hidden border-t border-[#F5F2EA]/10">
        <div className="absolute inset-0">
          <img src={COLUMNS} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-[#0D0D0D]/80" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8 py-32 text-center">
          <motion.div {...fade}>
            <MoireSymbol size={56} className="mx-auto mb-8 opacity-80" />
            <h2 className="font-serif text-5xl md:text-6xl text-[#F5F2EA]">What are you wearing?</h2>
            <div className="flex justify-center mt-12"><SnapButton testid="final-snap" /></div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
